import type { Logger } from './diagnostics';
import type { EkhoNamespace, LegacyEkhoChat } from './types';

const DEPRECATION_WARNING =
  '[Ekho] window.EkhoChat.init() is deprecated. The unified SDK auto-activates ' +
  'chat from server config — remove the manual init() call. This shim will be ' +
  'removed after all dealers migrate.';

/**
 * Backward-compat shim: the live dealer API is `window.EkhoChat.init({ domainOwnerId })`.
 * It now delegates to `window.Ekho.chat`, warns once, and replays any calls a
 * legacy IIFE loader buffered into `window.__ekhoPending` before the SDK loaded.
 */
export function installEkhoChatShim(ekho: EkhoNamespace, log: Logger): void {
  let warned = false;
  const shim: LegacyEkhoChat = {
    init(config) {
      if (!warned) {
        console.warn(DEPRECATION_WARNING);
        warned = true;
      }
      log('EkhoChat.init shim invoked', config?.domainOwnerId ?? 'no domainOwnerId');
      return {
        open: (message?: string): void => ekho.chat.open(message),
        destroy: (): void => ekho.chat.destroy(),
      };
    },
  };
  window.EkhoChat = shim;

  // `window.__ekhoPending` is the legacy IIFE loader's buffer (external
  // contract). Accessed via indexed Record so we neither augment Window with a
  // double-underscore global nor declare a property the naming rules reject.
  const legacyWindow = window as unknown as Record<string, unknown[] | undefined>;
  const pending = legacyWindow['__ekhoPending'];
  if (Array.isArray(pending)) {
    for (const config of pending) {
      shim.init(config as { domainOwnerId?: string });
    }
    legacyWindow['__ekhoPending'] = [];
  }
}
